/**
 * Shared deterministic Q&A generator for the business-law clusters
 * (corporate, estate, litigation, ip, pdpa).
 *
 * Rules enforced here:
 * - never emit a service price; quotes are requested by phone / LINE / e-mail
 * - always attach a "reviewed as of" statement + a "verify with the agency" note
 * - answers are self-contained so AI answer engines can quote a single entry
 */

export type BusinessQA = {
  slug: string;
  q: string;
  a: string;
  synonyms: string[];
  serviceSlug?: string;
};

export type QaService = {
  slug: string;
  nameTh: string;
  nameEn: string;
  turnaroundDays: number;
  keyDeliverables: string[];
  requiredDocs: string[];
  bestFor: string[];
  legalBasis: string;
};

export type QaAxis = {
  slug: string;
  nameTh: string;
  nameEn: string;
  note: string;
};

export const NO_PRICE_NOTE =
  "เว็บไซต์ไม่แสดงค่าบริการ กรุณาสอบถามขอบเขตงานและใบเสนอราคาทางโทรศัพท์ LINE หรืออีเมล";

export function asOf(reviewedTh: string, volatile: string): string {
  return `ข้อมูลทบทวน ณ ${reviewedTh} ${volatile} ${NO_PRICE_NOTE}`;
}

/** 8 intent angles per service — what / docs / steps / timeline / risks / foreign / compare / quote. */
export function serviceQA(
  s: QaService,
  ctx: { asOf: string; authority: string; clusterTh: string },
): BusinessQA[] {
  const A = ctx.asOf;
  const rows: Array<[string, string, string]> = [
    [
      "what",
      `${s.nameTh} คืออะไรและครอบคลุมงานใด`,
      `${s.nameTh} (${s.nameEn}) เป็นงานในกลุ่ม${ctx.clusterTh} ขอบเขตหลักคือ ${s.keyDeliverables.slice(0, 4).join(" · ")} เหมาะกับกรณี ${s.bestFor.slice(0, 2).join(" · ")} กรอบกฎหมายที่ใช้พิจารณาคือ ${s.legalBasis} ขอบเขตจริงต้องกำหนดจากข้อเท็จจริงและเอกสารของแต่ละราย ${A}`,
    ],
    [
      "documents",
      `${s.nameTh} ต้องเตรียมเอกสารอะไรบ้าง`,
      `เอกสารตั้งต้นได้แก่ ${s.requiredDocs.join(" · ")} เอกสารภาษาต่างประเทศต้องมีคำแปลและการรับรองตามที่หน่วยงานผู้พิจารณากำหนด ควรส่งสำเนาที่อ่านชัดครบทุกหน้าและเก็บต้นฉบับไว้แสดงต่อเจ้าหน้าที่ รายการอาจเพิ่มตามข้อเท็จจริงของเรื่อง ${A}`,
    ],
    [
      "steps",
      `${s.nameTh} มีขั้นตอนดำเนินการอย่างไร`,
      `ลำดับที่ลดความเสี่ยงมากที่สุดคือ ตรวจสถานะและวัตถุประสงค์ของเรื่อง จัดทำรายการเอกสารและเก็บหลักฐานให้ครบ ยกร่างหรือเตรียมคำขอให้ผู้มีอำนาจตรวจสอบ ยื่นต่อ${ctx.authority}หรือคู่กรณีตามช่องทางที่กำหนด แล้วติดตามผลพร้อมเก็บใบรับทุกขั้นตอน ${A}`,
    ],
    [
      "timeline",
      `${s.nameTh} ใช้เวลาประมาณเท่าไร`,
      `กรอบเวลาโดยทั่วไปอยู่ที่ประมาณ ${s.turnaroundDays} วันทำการนับจากวันที่เอกสารครบและได้รับคำยืนยันจากผู้รับบริการ แต่ระยะเวลาจริงขึ้นกับคิวพิจารณาของ${ctx.authority} ความครบถ้วนของเอกสาร และการขอข้อมูลเพิ่มเติม จึงควรเผื่อเวลาไว้และไม่กำหนดวันผูกพันกับคู่สัญญาก่อนได้รับผลจริง ${A}`,
    ],
    [
      "risks",
      `${s.nameTh} มีข้อควรระวังหรือสาเหตุที่มักถูกตีกลับอะไร`,
      `สาเหตุที่พบบ่อยคือ เอกสารไม่ครบหรือหมดอายุ ชื่อและตัวสะกดไม่ตรงกันระหว่างเอกสารแต่ละฉบับ ผู้ลงนามไม่มีอำนาจตามหลักฐานล่าสุด คำแปลไม่ผ่านการรับรองตามที่กำหนด และการยื่นโดยข้อมูลประมาณการที่ยังไม่มีหลักฐานรองรับ ควรตรวจสอบทั้งห้าข้อนี้ก่อนยื่นทุกครั้ง ${A}`,
    ],
    [
      "foreign",
      `${s.nameTh} สำหรับชาวต่างชาติหรือบริษัทต่างชาติต่างกันอย่างไร`,
      `ต่างที่ชุดหลักฐานและการรับรองเอกสาร เพราะต้องใช้เอกสารจากหน่วยงานต้นทางในต่างประเทศ คำแปลรับรอง และการรับรองเอกสารตามลำดับชั้นที่หน่วยงานผู้พิจารณายอมรับ นอกจากนี้อาจมีข้อจำกัดด้านสิทธิของคนต่างชาติในเรื่องนั้นตาม ${s.legalBasis} จึงควรตรวจสิทธิและรูปแบบเอกสารก่อนวางแผนดำเนินการ ${A}`,
    ],
    [
      "diy",
      `${s.nameTh} ดำเนินการเองได้หรือควรให้ทนายหรือผู้เชี่ยวชาญช่วย`,
      `บางขั้นตอนดำเนินการเองได้หากเข้าใจแบบฟอร์มและเงื่อนไข แต่กรณีที่มีคู่กรณี มีกำหนดเวลาตามกฎหมาย มีเอกสารต่างประเทศ หรือมีผลผูกพันระยะยาว การให้ผู้เชี่ยวชาญตรวจร่างและวางลำดับงานช่วยลดความเสี่ยงที่แก้ไขภายหลังได้ยาก ผลลัพธ์ยังขึ้นอยู่กับดุลพินิจของ${ctx.authority}ในทุกกรณี ${A}`,
    ],
    [
      "quote",
      `${s.nameTh} คิดค่าบริการอย่างไรและขอใบเสนอราคาได้ทางไหน`,
      `ค่าบริการขึ้นกับความซับซ้อนของข้อเท็จจริง จำนวนเอกสาร จำนวนคู่กรณีหรือหน่วยงานที่เกี่ยวข้อง ภาษาที่ใช้ และความเร่งด่วน เว็บไซต์จึงไม่แสดงราคาเหมารวมเพื่อไม่ให้เข้าใจผิด เจ้าหน้าที่จะประเมินขอบเขตและส่งใบเสนอราคาหลังตรวจข้อมูลเบื้องต้นทางโทรศัพท์ LINE หรืออีเมล ${A}`,
    ],
  ];
  return rows.map(([suffix, q, a]) => ({
    slug: `${s.slug}-${suffix}`,
    q,
    a,
    synonyms: [`${s.nameTh} ${suffix}`, `${s.nameEn.toLowerCase()} ${suffix}`],
    serviceSlug: s.slug,
  }));
}

/** service × axis matrix — 3 intents per pair (docs / process / risk). */
export function matrixQA(
  services: readonly QaService[],
  axis: readonly QaAxis[],
  ctx: { asOf: string; authority: string; axisLabelTh: string },
): BusinessQA[] {
  const out: BusinessQA[] = [];
  for (const s of services) {
    for (const a of axis) {
      out.push({
        slug: `${s.slug}-${a.slug}-docs`,
        q: `${s.nameTh} สำหรับ${a.nameTh} ต้องเตรียมเอกสารหรือข้อมูลเพิ่มอะไร`,
        a: `นอกจากชุดเอกสารมาตรฐาน (${s.requiredDocs.slice(0, 4).join(" · ")}) กรณี${a.nameTh}มีประเด็นเฉพาะคือ ${a.note} จึงควรเตรียมหลักฐานที่รองรับประเด็นดังกล่าวไว้ล่วงหน้า และยืนยันรูปแบบเอกสารที่${ctx.authority}ยอมรับก่อนยื่น ${ctx.asOf}`,
        synonyms: [`${s.nameTh} ${a.nameTh} เอกสาร`, `${s.nameEn} ${a.nameEn} documents`],
        serviceSlug: s.slug,
      });
      out.push({
        slug: `${s.slug}-${a.slug}-process`,
        q: `${a.nameTh}ควรวางลำดับงาน ${s.nameTh} อย่างไร`,
        a: `เริ่มจากประเมินประเด็นเฉพาะของ${a.nameTh} ซึ่งโดยทั่วไปคือ ${a.note} จากนั้นรวบรวมเอกสารและคำแปลรับรองให้ครบชั้น ยกร่างหรือเตรียมคำขอให้ผู้มีอำนาจตรวจ แล้วจึงยื่นและเผื่อเวลาประมาณ ${s.turnaroundDays} วันทำการหลังเอกสารครบ ยึด ${s.legalBasis} เป็นกรอบพิจารณา ${ctx.asOf}`,
        synonyms: [`${s.nameTh} ขั้นตอน ${a.nameTh}`, `${s.nameEn} process ${a.nameEn}`],
        serviceSlug: s.slug,
      });
      out.push({
        slug: `${s.slug}-${a.slug}-risk`,
        q: `${s.nameTh} ใน${ctx.axisLabelTh} ${a.nameTh} มีความเสี่ยงอะไรที่ต้องระวัง`,
        a: `ความเสี่ยงหลักมาจากประเด็นเฉพาะที่ว่า ${a.note} หากประเมินต่ำเกินไปอาจทำให้เอกสารถูกตีกลับ เสียกำหนดเวลา หรือได้ผลไม่ตรงวัตถุประสงค์ ควรตรวจอำนาจผู้ลงนาม ความสอดคล้องของชื่อและวันที่ในทุกฉบับ และเงื่อนไขล่าสุดของ${ctx.authority}ก่อนดำเนินการ ${ctx.asOf}`,
        synonyms: [`${s.nameTh} ความเสี่ยง ${a.nameTh}`, `${s.nameEn} risk ${a.nameEn}`],
        serviceSlug: s.slug,
      });
    }
  }
  return out;
}

/** Merge, de-duplicate by slug, and cap the corpus size. */
export function buildQA(parts: BusinessQA[][], cap = 1500): BusinessQA[] {
  const seen = new Set<string>();
  const out: BusinessQA[] = [];
  for (const part of parts) {
    for (const qa of part) {
      if (seen.has(qa.slug)) continue;
      seen.add(qa.slug);
      out.push(qa);
      if (out.length >= cap) return out;
    }
  }
  return out;
}

export function phrasingCount(list: readonly BusinessQA[]): number {
  return list.reduce((n, qa) => n + 1 + qa.synonyms.length, 0);
}
